import {
  CLIENT_MESSAGE_TYPES,
  SERVER_MESSAGE_TYPES,
} from "../../../shared/protocol.js";
import { DEFAULT_CHARACTER_ID } from "../../../shared/characterCatalog.js";

export class NetClient {
  constructor({
    url,
    roomId,
    name = "Player",
    characterId = DEFAULT_CHARACTER_ID,
    getSessionId = () => null,
    onStatus = () => {},
    onWelcome = () => {},
    onState = () => {},
    onPlayerJoined = () => {},
    onPlayerLeft = () => {},
    onProjectileSpawn = () => {},
    onProjectileHit = () => {},
    onCombatHit = () => {},
    onCombatSwing = () => {},
    onChatMessage = () => {},
    onBulletinBoardSnapshot = () => {},
    reconnectDelayMs = 1500,
  }) {
    this.url = url;
    this.roomId = roomId;
    this.name = name;
    this.characterId = characterId;
    this.getSessionId = getSessionId;
    this.onStatus = onStatus;
    this.onWelcome = onWelcome;
    this.onState = onState;
    this.onPlayerJoined = onPlayerJoined;
    this.onPlayerLeft = onPlayerLeft;
    this.onProjectileSpawn = onProjectileSpawn;
    this.onProjectileHit = onProjectileHit;
    this.onCombatHit = onCombatHit;
    this.onCombatSwing = onCombatSwing;
    this.onChatMessage = onChatMessage;
    this.onBulletinBoardSnapshot = onBulletinBoardSnapshot;
    this.reconnectDelayMs = reconnectDelayMs;

    this.ws = null;
    this.isStopped = false;
    this.reconnectTimer = null;
    this.heartbeatTimer = null;
    this.lastRttMs = null;
  }

  connect() {
    if (this.isStopped) return;
    if (this.ws && (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING)) {
      return;
    }

    this.onStatus("connecting");
    this.ws = new WebSocket(this.url);

    this.ws.addEventListener("open", () => {
      this.onStatus("connected");
      this.send({
        type: CLIENT_MESSAGE_TYPES.JOIN,
        roomId: this.roomId,
        name: this.name,
        characterId: this.characterId,
        sessionId: this.getSessionId(),
      });
      this.startHeartbeat();
    });

    this.ws.addEventListener("message", (event) => {
      this.handleMessage(event.data);
    });

    this.ws.addEventListener("close", () => {
      this.stopHeartbeat();
      if (this.isStopped) {
        this.onStatus("closed");
        return;
      }
      this.onStatus("reconnecting");
      this.scheduleReconnect();
    });

    this.ws.addEventListener("error", () => {
      this.onStatus("error");
    });
  }

  stop() {
    this.isStopped = true;
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    this.stopHeartbeat();
    if (this.ws) {
      try {
        this.ws.close();
      } catch {
        // no-op
      }
      this.ws = null;
    }
    this.onStatus("stopped");
  }

  sendInput(seq, input) {
    const payload = {
      ...input,
      rttMs: Number.isFinite(this.lastRttMs) ? this.lastRttMs : undefined,
    };
    this.send({ type: CLIENT_MESSAGE_TYPES.INPUT, seq, input: payload });
  }

  sendProjectileSpawn(projectile) {
    this.send({ type: CLIENT_MESSAGE_TYPES.PROJECTILE_SPAWN, projectile });
  }

  sendHeal(amount) {
    this.send({ type: CLIENT_MESSAGE_TYPES.HEAL, amount });
  }

  sendChat(text) {
    this.send({
      type: CLIENT_MESSAGE_TYPES.CHAT_MESSAGE,
      text,
      clientTime: Date.now(),
      mapId: this.roomId,
    });
  }

  sendScoreEvent(event, details = null) {
    if (typeof event !== "string") return;
    const safeEvent = event.trim().toLowerCase();
    if (!safeEvent) return;
    const payload = { type: CLIENT_MESSAGE_TYPES.SCORE_EVENT, event: safeEvent };
    if (details && typeof details === "object" && !Array.isArray(details)) {
      Object.assign(payload, details);
    }
    this.send(payload);
  }

  sendBulletinBoardCreate(text) {
    this.send({
      type: CLIENT_MESSAGE_TYPES.BULLETIN_BOARD_CREATE,
      text,
      clientTime: Date.now(),
      mapId: this.roomId,
    });
  }

  sendBulletinBoardUpdate(messageId, text) {
    this.send({
      type: CLIENT_MESSAGE_TYPES.BULLETIN_BOARD_UPDATE,
      messageId,
      text,
      clientTime: Date.now(),
      mapId: this.roomId,
    });
  }

  sendBulletinBoardDelete(messageId) {
    this.send({
      type: CLIENT_MESSAGE_TYPES.BULLETIN_BOARD_DELETE,
      messageId,
      clientTime: Date.now(),
      mapId: this.roomId,
    });
  }

  send(payload) {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return;
    this.ws.send(JSON.stringify(payload));
  }

  handleMessage(raw) {
    let msg;
    try {
      msg = JSON.parse(raw);
    } catch {
      return;
    }
    if (!msg || typeof msg.type !== "string") return;

    switch (msg.type) {
      case SERVER_MESSAGE_TYPES.WELCOME:
        this.onWelcome(msg);
        break;
      case SERVER_MESSAGE_TYPES.STATE:
        this.onState(msg);
        break;
      case SERVER_MESSAGE_TYPES.PLAYER_JOINED:
        this.onPlayerJoined(msg);
        break;
      case SERVER_MESSAGE_TYPES.PLAYER_LEFT:
        this.onPlayerLeft(msg);
        break;
      case SERVER_MESSAGE_TYPES.PROJECTILE_SPAWN:
        this.onProjectileSpawn(msg);
        break;
      case SERVER_MESSAGE_TYPES.PROJECTILE_HIT:
        this.onProjectileHit(msg);
        break;
      case SERVER_MESSAGE_TYPES.COMBAT_HIT:
        this.onCombatHit(msg);
        break;
      case SERVER_MESSAGE_TYPES.COMBAT_SWING:
        this.onCombatSwing(msg);
        break;
      case SERVER_MESSAGE_TYPES.CHAT_MESSAGE:
        this.onChatMessage(msg);
        break;
      case SERVER_MESSAGE_TYPES.BULLETIN_BOARD_SNAPSHOT:
        this.onBulletinBoardSnapshot(msg);
        break;
      case SERVER_MESSAGE_TYPES.PONG:
        if (Number.isFinite(msg?.clientTime)) {
          this.lastRttMs = Math.max(0, Date.now() - Number(msg.clientTime));
        }
        break;
      case SERVER_MESSAGE_TYPES.ERROR:
      default:
        break;
    }
  }

  scheduleReconnect() {
    if (this.reconnectTimer || this.isStopped) return;
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.connect();
    }, this.reconnectDelayMs);
  }

  startHeartbeat() {
    this.stopHeartbeat();
    this.heartbeatTimer = setInterval(() => {
      this.send({ type: CLIENT_MESSAGE_TYPES.PING, clientTime: Date.now() });
    }, 4000);
  }

  stopHeartbeat() {
    if (!this.heartbeatTimer) return;
    clearInterval(this.heartbeatTimer);
    this.heartbeatTimer = null;
  }
}
